import type {ReactElement} from 'react';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {CHECKBOX_TYPE} from '@Components/checkbox-legacy';
import {useEventWizardTicketingConfig, useEventWizardTicketingFees} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardTicketingPassFeesToggle} from '@Components/event-wizard/event-wizard-slice';
import {computeTicketPriceBreakdown} from '@Components/event-wizard/event-wizard-ticketing/ticketing-fees.helper';
import {openTicketingPriceBreakdownModal} from '@Components/modals/ticketing-price-breakdown-modal/ticketing-price-breakdown-modal.library';
import {formatNumberInUSD} from '@Utils/number.util';
import {useAppDispatch} from '@/hooks';
import type {TicketingPassFeesToggleProps} from './ticketing-pass-fees-toggle.types';
import styles from './ticketing-pass-fees-toggle.module.scss';

/**
 * "Pass fees to buyer", on the Tickets row. Fees sit on whatever the event charges — the ticket price, a donation, or
 * both — so the toggle applies event-wide. RSVP has no toggle: its donation fees always go to the buyer.
 */
export function TicketingPassFeesToggle({isHidden, className = ''}: TicketingPassFeesToggleProps): ReactElement {
  const dispatch = useAppDispatch();
  const config = useEventWizardTicketingConfig();
  const ticketingFees = useEventWizardTicketingFees();

  // Computed in both fee modes, not just when fees are passed on: the organiser's payout moves with the toggle,
  // so the line under it has something to say either way. Per ticket, so only a priced ticket has one.
  const priceBreakdown = !config.isRsvp && config.price > 0 && ticketingFees !== null ? computeTicketPriceBreakdown(config, ticketingFees) : null;

  const getViewDetailsLink = (): ReactElement => {
    return (
      <ClickableDiv
        className="flexbox _cursor-pointer"
        onClick={() => {
          openTicketingPriceBreakdownModal();
        }}
      >
        <Text className="ct-brand-primary" val={window.i18next.t('pmwjs_event_ticketing_view_details')} size={TextSize.SM} bold underline />
      </ClickableDiv>
    );
  };

  /**
   * The organiser's own money, sitting directly under the toggle that changes it — passing fees on is what makes
   * this figure whole, so it belongs here rather than a buyer-facing total. Reads `payout`, the same field the
   * View-details panel shows against "You receive", so the two can never disagree; that also means it carries any
   * tax, which flows to the organiser to remit. Absent until the fee params resolve, since payout needs them.
   */
  const getOrganiserPayoutLine = (): ReactElement | null => {
    if (priceBreakdown === null) {
      return null;
    }

    return (
      <div className={`${styles.passFeesSubtitle} flex-row-align-center flex-wrap gap-1`}>
        <Text
          className="ct-neutral-secondary"
          val={window.i18next.t('pmwjs_event_ticketing_you_receive_per_ticket', {amount: formatNumberInUSD(priceBreakdown.payout)})}
          size={TextSize.SM}
        />
        {getViewDetailsLink()}
      </div>
    );
  };

  return (
    <div className={`${styles.passFeesControl} ${isHidden ? styles.passFeesControlHidden : ''} ${className}`}>
      <LabelledCheckbox
        className="flex-1"
        checkboxType={CHECKBOX_TYPE.PRIMARY_LIGHT}
        label={window.i18next.t('pmwjs_event_ticketing_pass_fees')}
        isChecked={config.passFeesToBuyer}
        onChange={(isChecked: boolean): void => {
          dispatch(onEventWizardTicketingPassFeesToggle(isChecked));
        }}
      />
      {getOrganiserPayoutLine()}
    </div>
  );
}
